import { ExpenseStatusEnum, RightEnum, Vendor, VendorRule, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Expense } from "@finway-group/shared/lib/models/expense/expense.model"
import { Button, Col, Form, Row } from "antd"
import { FormInstance } from "antd/lib/form"
import React from "react"
import { useTranslation } from "react-i18next"
import { ExpenseRules } from "~Components/forms/rules"
import { NewVendorDetectedIcon } from "~Components/icons/NewVendorDetectedIcon"
import { ExpenseData } from "~Components/modals/expenseCreateForm.context"
import { VendorFormModal } from "~Components/modals/vendor.modal"
import { VendorRuleModal } from "~Components/modals/vendorRule.modal"
import { useModal } from "~Shared/context/modal.context"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useUserCreditorById, useUserCreditors } from "~Shared/hooks/vendor.hooks"
import { useExpenseVendor } from "~Shared/queries/vendors/vendor.queries"
import { AuthzService } from "~Shared/services"
import { isHospitalityOrStandardReimbursement, isMileageOrPerDiem } from "~Shared/utils/expense.utils"
import { VendorSelect } from "./VendorSelect"

const MINIMUM_VENDOR_RULES = 4

interface VendorSelectFieldProps {
    expense: Expense<string>

    updateExpense: (data: ExpenseData) => void
    expenseForm: FormInstance<
        | ExpenseData
        | {
              [key: string]: any
          }
    >
    setPrefilledVendorData: (prefilledVendorData: any) => void

    prefilledVendorData?: { name?: string }
}

export const VendorSelectField = ({ expense, updateExpense, prefilledVendorData, expenseForm, setPrefilledVendorData }: VendorSelectFieldProps) => {
    const { vendor: vendorRules } = ExpenseRules({ expense })
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()
    const { t } = useTranslation()
    const { showModal } = useModal()
    const { data: selectedVendor } = useExpenseVendor(expense)
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))

    const selectedUserCreditor = useUserCreditorById(expense?.creditorUser?._id)
    const selectedCreditor = expense.vendorType === VendorTypeEnum.VENDOR ? selectedVendor : selectedUserCreditor

    const isExpenseHospitalityOrStandardReimbursement = isHospitalityOrStandardReimbursement(expense)
    const getVendorRuleCount = () => {
        const rules = selectedVendor?.rule ?? {}
        const numberOfRules = Object.values(rules).filter((rule) => (Array.isArray(rule) ? rule.length > 0 : !!rule)).length
        // if rules is not the empty object, it includes the id field which is not a rule and is therefore not included in the rule count
        return numberOfRules === 0 ? numberOfRules : numberOfRules - 1
    }
    const shouldShowOnlyVerifiedVendors = [ExpenseStatusEnum.DONE, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DECLINED, ExpenseStatusEnum.REVIEWED].includes(expense.status)
    const onVendorCreation = (_flag: boolean, vendor: Vendor) => {
        if (vendor) {
            expenseForm.setFieldsValue({ vendor: vendor.id })
            updateExpense({ vendor: { _id: vendor.id } })
            setPrefilledVendorData(undefined)
        }
    }
    const showVendorRuleModal = (vendor: Vendor) => {
        showModal(VendorRuleModal, true, {
            isShowing: true,
            vendor,
            onSubmit: (rules: Pick<VendorRule, "costCenter" | "costCenter2" | "expenseAccount" | "expensePaymentFlowInformation" | "paymentTerm">) => {
                updateExpense({
                    costCenter: rules.costCenter,
                    costCenter2: rules.costCenter2,
                    expenseAccount: rules.expenseAccount ? { _id: rules.expenseAccount } : undefined,
                    paymentFlowInformation: rules.expensePaymentFlowInformation,
                })
            },
        })
    }

    const renderVendorLabel = () => {
        const canSeeVendorRules = AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ)
        const isVendorCreditor = selectedCreditor && selectedCreditor instanceof Vendor

        return (
            <>
                {isVendorCreditor ? (
                    <span className={`${canSeeVendorRules ? "cursor-pointer" : ""}`} onClick={() => canSeeVendorRules && showVendorRuleModal(selectedCreditor)}>
                        {`${t("input:request.vendor")} `}
                        {canSeeVendorRules && (
                            <u className="rule-link">{`(${t("label:rules")} ${getVendorRuleCount()}/${
                                MINIMUM_VENDOR_RULES + Number(costCenters2.length > 0) + Number(expenseAccounts.length > 0)
                            })`}</u>
                        )}
                    </span>
                ) : (
                    <span>{t("input:request.vendor")}</span>
                )}
            </>
        )
    }

    const onVendorCreditorChange = (creditorId: string, vendorType: VendorTypeEnum = VendorTypeEnum.VENDOR, vendorName?: string) => {
        if (vendorType === VendorTypeEnum.VENDOR) {
            const updateObject: any = { vendor: { _id: creditorId, ...(vendorName && { name: vendorName }) }, creditorUser: null }
            updateExpense({ ...updateObject, vendorType })
        } else {
            const updateObject = {
                vendor: undefined,
                creditorUser: { _id: creditorId },
            }

            updateExpense({ ...updateObject, vendorType })
        }
    }

    const showVendorModal = () => {
        showModal(VendorFormModal, true, {
            isShowing: true,
            isNew: true,
            prefilledData: prefilledVendorData,
            onCancel: onVendorCreation,
            shouldRedirectOnRestoration: false,
            isDraggable: true,
            isOpenedByExpenseModal: true,
        })
    }

    if (isMileageOrPerDiem(expense)) {
        return null
    }

    return (
        <Row gutter={[16, 16]} align="top">
            <Col span={22}>
                <VendorSelect
                    shouldShowOnlyVerifiedVendors={shouldShowOnlyVerifiedVendors}
                    shouldRenderSelectDropdown={isExpenseHospitalityOrStandardReimbursement}
                    selectedVendorType={expense?.vendorType === VendorTypeEnum.USER && selectedCreditor ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR}
                    userCreditors={userCreditors}
                    selectedCreditorId={selectedCreditor?.id}
                    onSelect={onVendorCreditorChange}
                    formProps={{
                        style: { marginBottom: "0px" },
                        label: renderVendorLabel(),
                        name: [expense?.vendorType === VendorTypeEnum.USER ? "creditorUser" : "vendor", "_id"],
                        rules: vendorRules,
                        extra: <p className="input-help">{t("info:vendor_rule")}</p>,
                    }}
                />
            </Col>
            {
                <Col span={2} className="mb-8 h-full">
                    {/* need empty label for proper alignment */}
                    <Form.Item label=" ">
                        <Button icon={<NewVendorDetectedIcon isHighlighted={!!prefilledVendorData?.name} />} className="btn-default" onClick={showVendorModal} />
                    </Form.Item>
                </Col>
            }
        </Row>
    )
}
